/**
 * SearchrManager.ts - SearchrManager - Universal search system
 * 
 * CHANGE HISTORY:
 * - 251021_2138 [3dbdca8] danielbeer: fix: Remove all remaining duplicate type annotations in searchr and draggr
 * - 251021_2028 [c5f5984] danielbeer: fix: Resolve additional TypeScript syntax errors
 * - 251021_0517 [8786ca9] danielbeer: feat: apply authr pattern to all 43 remaining packages
 * - 251021_0509 [c090082] danielbeer: feat: complete remaining TypeScript fixes across all packages
 * - 251021_0452 [97a0397] danielbeer: fix: correct automated type annotations and test scripts
 * - 251021_0447 [ce7b5e6] danielbeer: feat: automate TypeScript and Actr integration for all packages
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
import { nanoid } from 'nanoid';


interface SearchrManagerOptions {
  actr?: any; // Actr instance
  [key: string]: any;
}

export class SearchrManager {
  private actr: any;
  constructor(options: SearchrManagerOptions = {}) {
    this.actr = options.actr || this._tryGetActr();
    this.options = {
      enableNaturalLanguage: true,
      enableAdvancedSearch: true,
      maxResults: 100,
      timeout: 5000, // 5 seconds
      cacheResults: true,
      cacheTTL: 300000, // 5 minutes
      ...options
    };
    
    // Search adapters
    this.adapters = new Map();
    
    // Search scopes
    this.scopes = {
      CURRENT_VIEW: 'current_view',
      CURRENT_DATASET: 'current_dataset',
      ALL_DATASETS: 'all_datasets',
      EXTERNAL: 'external'
    };
    
    // Current context
    this.currentView = null;
    this.currentDataset = null;
    this.loadedDatasets = new Set();
    
    // Search history
    this.history = [];
    this.maxHistorySize = 50;
    
    // Saved searches
    this.savedSearches = new Map();
    
    // Result cache
    this.cache = new Map();
    
    // Freshness tracking
    this.freshnessScores = new Map();
    
    // Event listeners
    this.listeners = new Set();
    
    // Active searches
    this.activeSearches = new Map();
    
    // Register actions with Actr if available
    if (this.actr) {
      this.registerActions();
    }
  }
  
  /**
   * Try to get Actr from Dependr (optional)
   */
  private _tryGetActr() {
    try {
      const { getActr } = require('@opcentral/actr');
      return getActr();
    } catch {
      return null;
    }
  }

  /**
   * Register Searchr actions with Actr
   * 
   * ARCHITECTURE NOTE: Searchr uses ACTIONS-ONLY pattern
   * 
   * Why this is different from notifyr/groupr:
   * 1. Ephemeral Results: Search results are temporary
   *    - Results cached for short TTL (5 minutes)
   *    - Stale quickly as data changes
   *    - No persistence needed - re-search when needed
   *    - CRUD would add unnecessary overhead
   * 
   * 2. External Data Sources: Search queries external systems
   *    - Adapters connect to external search engines
   *    - OpCentral doesn't own the search data
   *    - External systems are source of truth
   *    - No need for OpCentral CRUD
   * 
   * 3. High-Frequency Operations: Search called constantly
   *    - Every keystroke in search box (debounced)
   *    - Performance-critical (must be fast)
   *    - CRUD overhead would degrade UX
   * 
   * Pattern: Actions-Only (No CRUD)
   * - Actions: For search operations (search, refresh, natural language)
   * - No CRUD: Results are ephemeral, cached temporarily
   * - Adapter-based: External systems queried via adapters
   * 
   * Compare to other patterns:
   * - loggr: Actions-Only (write-heavy, external integrations)
   * - clippr: Actions-Only (ephemeral, OS-integrated)
   * - searchr: Actions-Only (ephemeral results, external data)
   * 
   * See: /docs/ACTR_INTEGRATION_PATTERNS.md for complete explanation
   * See: /packages/searchr/README.md "Actr Integration" section
   */
  private registerActions(): any {
    if (!this.actr) return;
    
    // search action (high-frequency)
    this.actr.register('searchr:search', {
      handler: async (params) => {
        return await this.search(params);
      },
      description: 'Perform search across configured sources'
    });

    // searchCurrentView action
    this.actr.register('searchr:search-current-view', {
      handler: async (params) => {
        return await this.searchCurrentView(params);
      },
      description: 'Search within current view'
    });

    // searchCurrentDataset action
    this.actr.register('searchr:search-current-dataset', {
      handler: async (params) => {
        return await this.searchCurrentDataset(params);
      },
      description: 'Search within current dataset'
    });

    // searchAllDatasets action
    this.actr.register('searchr:search-all-datasets', {
      handler: async (params) => {
        return await this.searchAllDatasets(params);
      },
      description: 'Search across all datasets'
    });

    // searchExternal action
    this.actr.register('searchr:search-external', {
      handler: async (params) => {
        return await this.searchExternal(params);
      },
      description: 'Search external sources'
    });

    // searchData action
    this.actr.register('searchr:search-data', {
      handler: async (params) => {
        return await this.searchData(params);
      },
      description: 'Search data with filters'
    });

    // naturalLanguageSearch action
    this.actr.register('searchr:natural-language-search', {
      handler: async (params) => {
        return await this.naturalLanguageSearch(params);
      },
      description: 'Natural language search'
    });

    // refreshAdapter action
    this.actr.register('searchr:refresh-adapter', {
      handler: async (params) => {
        return await this.refreshAdapter(params);
      },
      description: 'Refresh search adapter'
    });
    
    // Note: No CRUD operations - search results are ephemeral and cached temporarily.
    // External search engines (adapters) are queried on-demand, not persisted.
  }

  /**
   * Register a search adapter
   */
  registerAdapter(name: string, adapter): void {
    this.adapters.set(name, adapter);
    
    // Initialize freshness score
    if (!this.freshnessScores.has(name)) {
      this.freshnessScores.set(name, {
        score: 1.0,
        lastRefresh: Date.now(),
        autoRefresh: adapter.autoRefresh !== false,
        refreshInterval: adapter.refreshInterval || 300000 // 5 minutes default
      });
    }
  }
  
  /**
   * Set current context
   */
  setContext(view, dataset): void {
    this.currentView = view;
    this.currentDataset = dataset;
    
    if (dataset) {
      this.loadedDatasets.add(dataset);
    }
  }
  
  /**
   * Multi-level search
   */
  async search(query, options): Promise<any> {
    const {
      scope = this.scopes.CURRENT_VIEW,
      type = 'text', // 'text', 'advanced', 'natural'
      filters = {},
      maxResults = this.options.maxResults,
      timeout = this.options.timeout
    } = options;
    
    const searchId = nanoid();
    
    // Add to history
    this.addToHistory(query, options);
    
    // Check cache
    const cacheKey = this.getCacheKey(query, options);
    if (this.options.cacheResults) {
      const cached = this.cache.get(cacheKey);
      if (Date.now() - cached.timestamp < this.options.cacheTTL) {
        return cached.results;
      }
    }
    
    // Start search
    this.activeSearches.set(searchId, {
      query,
      options,
      startTime: Date.now()
    });
    
    try {
      let results;
      
      switch (scope) {
        case this.scopes.CURRENT_VIEW:
          results = await this.searchCurrentView(query, type, filters, timeout);
          break;
          
        case this.scopes.CURRENT_DATASET:
          results = await this.searchCurrentDataset(query, type, filters, timeout);
          break;
          
        case this.scopes.ALL_DATASETS:
          results = await this.searchAllDatasets(query, type, filters, timeout);
          break;
          
        case this.scopes.EXTERNAL:
          results = await this.searchExternal(query, type, filters, timeout);
          break;
          
        default:
          throw new Error(`Unknown search scope: ${scope}`);
      }
      
      // Limit results
      results = results.slice(0, maxResults);
      
      // Cache results
      if (this.options) {
        this.cache.set(cacheKey, {
          results,
          timestamp: Date.now()
        });
      }
      
      // Notify listeners
      this.notifyListeners('searchComplete', {
        searchId,
        query,
        scope,
        resultCount: results.length
      });
      
      return results;
      
    } catch (error) {
      this.notifyListeners('searchError', {
        searchId,
        query,
        error: error.message
      });
      throw error;
      
    } finally {
      this.activeSearches.delete(searchId);
    }
  }
  
  /**
   * Search current view
   */
  async searchCurrentView(query, type, filters, timeout): Promise<any> {
    if (!this.currentView) {
      throw new Error('No current view set');
    }
    
    // Get view data
    const viewData = this.currentView.getData();
    
    return this.searchData(viewData, query, type, filters);
  }
  
  /**
   * Search current dataset
   */
  async searchCurrentDataset(query, type, filters, timeout): Promise<any> {
    if (!this.currentDataset) {
      throw new Error('No current dataset set');
    }
    
    // Get dataset data
    const datasetData = this.currentDataset.getAllData();
    
    return this.searchData(datasetData, query, type, filters);
  }
  
  /**
   * Search all loaded datasets
   */
  async searchAllDatasets(query, type, filters, timeout): Promise<any> {
    const allResults = [];
    
    for (const dataset of this.loadedDatasets) {
      try {
        const data = dataset.getAllData();
        const results = await this.searchData(data, query, type, filters);
        
        // Add dataset info to results
        results.forEach(result => {
          result._dataset = dataset.id;
          result._datasetName = dataset.name;
        });
        
        allResults.push(...results);
      } catch (error) {
        console.error(`Error searching dataset ${dataset.id}:`, error);
      }
    }
    
    // Rank and deduplicate
    return this.rankResults(allResults, query);
  }
  
  /**
   * Search external sources
   */
  async searchExternal(query, type, filters, timeout): Promise<any> {
    const allResults = [];
    const searchPromises = [];
    
    // Search all registered adapters
    for (const [name, adapter] of this.adapters) {
      const freshness = this.freshnessScores.get(name);
      
      // Check if refresh needed
      if (freshness.autoRefresh) {
        const timeSinceRefresh = Date.now() - freshness.lastRefresh;
        if (timeSinceRefresh > freshness.refreshInterval) {
          // Trigger refresh
          this.refreshAdapter(name);
        }
      }
      
      // Create search promise with timeout
      const searchPromise = Promise.race([
        adapter.search(query, type, filters),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Search timeout')), timeout)
        )
      ]).then(results => {
        // Add source info
        results.forEach(result => {
          result._source = name;
          result._freshness = freshness.score;
        });
        return results;
      }).catch(error => {
        console.error(`Error searching ${name}:`, error);
        return [];
      });
      
      searchPromises.push(searchPromise);
    }
    
    // Wait for all searches
    const resultsArrays = await Promise.all(searchPromises);
    
    // Flatten and combine
    resultsArrays.forEach(results => {
      allResults.push(...results);
    });
    
    // Rank by relevance and freshness
    return this.rankResults(allResults, query);
  }
  
  /**
   * Search data with different types
   */
  async searchData(data, query, type, filters): Promise<any> {
    switch (type) {
      case 'text':
        return this.textSearch(data, query, filters);
        
      case 'advanced':
        return this.advancedSearch(data, query, filters);
        
      case 'natural':
        return this.naturalLanguageSearch(data, query, filters);
        
      default:
        throw new Error(`Unknown search type: ${type}`);
    }
  }
  
  /**
   * Text search
   */
  textSearch(data, query, filters): any {
    const queryLower = query.toLowerCase();
    
    return data.filter(item => {
      // Apply filters first
      if (!this.matchesFilters(item, filters)) {
        return false;
      }
      
      // Search in searchable fields
      const searchableFields = this.getSearchableFields(item);
      
      return searchableFields.some(field => {
        const value = this.getFieldValue(item, field);
        if (!value) return false;
        
        return value.toLowerCase().includes(queryLower);
      });
    });
  }
  
  /**
   * Advanced search with operators
   */
  advancedSearch(data, query, filters): any {
    // Parse query for operators (AND, OR, NOT, field:value)
    const parsedQuery = this.parseAdvancedQuery(query);
    
    return data.filter(item => {
      // Apply filters
      if (!this.matchesFilters(item, filters)) {
        return false;
      }
      
      // Evaluate query
      return this.evaluateQuery(item, parsedQuery);
    });
  }
  
  /**
   * Natural language search
   */
  async naturalLanguageSearch(data, query, filters): Promise<any> {
    // This would integrate with AI for semantic search
    // For now, fall back to text search with fuzzy matching
    
    const results = data.filter(item => {
      if (!this.matchesFilters(item, filters)) {
        return false;
      }
      
      const searchableFields = this.getSearchableFields(item);
      
      return searchableFields.some(field => {
        const value = this.getFieldValue(item, field);
        if (!value) return false;
        
        // Fuzzy match
        return this.fuzzyMatch(value, query, 0.6);
      });
    });
    
    return this.rankResults(results, query);
  }
  
  /**
   * Parse advanced query
   */
  parseAdvancedQuery(query): any {
    // Simple parser for: field:value, AND, OR, NOT, "exact phrase"
    const tokens = [];
    const regex = /(\w+):("([^"]+)"|(\S+))|"([^"]+)"|(\S+)/g;
    let match;
    
    while ((match = regex.exec(query)) !== null) {
      if (match[1]) {
        // Field search
        tokens.push({
          type: 'field',
          field: match[1],
          value: match[3] || match[4]
        });
      } else if (match[5]) {
        // Exact phrase
        tokens.push({
          type: 'phrase',
          value: match[5]
        });
      } else if (match[6]) {
        // Word or operator
        const word = match[6].toUpperCase();
        if (['AND', 'OR', 'NOT'].includes(word)) {
          tokens.push({ type: 'operator', value: word });
        } else {
          tokens.push({ type: 'word', value: match[6] });
        }
      }
    }
    
    return tokens;
  }
  
  /**
   * Evaluate parsed query
   */
  evaluateQuery(item, tokens): any {
    // Simple evaluation (can be enhanced)
    let result = true;
    let operator = 'AND';
    
    for (const token of tokens) {
      let matches = false;
      
      switch (token.type) {
        case 'field':
          const fieldValue = this.getFieldValue(item, token.field);
          matches = fieldValue && fieldValue.toLowerCase().includes(token.value.toLowerCase());
          break;
          
        case 'phrase':
        case 'word':
          const searchableFields = this.getSearchableFields(item);
          matches = searchableFields.some(field => {
            const value = this.getFieldValue(item, field);
            return value && value.toLowerCase().includes(token.value.toLowerCase());
          });
          break;
          
        case 'operator':
          operator = token.value;
          continue;
      }
      
      // Apply operator
      switch (operator) {
        case 'AND':
          result = result && matches;
          break;
        case 'OR':
          result = result || matches;
          break;
        case 'NOT':
          result = result && !matches;
          break;
      }
      
      operator = 'AND'; // Reset
    }
    
    return result;
  }
  
  /**
   * Fuzzy match
   */
  fuzzyMatch(str, pattern, threshold = 0.6): any {
    const distance = this.levenshteinDistance(
      str.toLowerCase(),
      pattern.toLowerCase()
    );
    const maxLength = Math.max(str.length, pattern.length);
    const similarity = 1 - (distance / maxLength);
    
    return similarity >= threshold;
  }
  
  /**
   * Levenshtein distance
   */
  levenshteinDistance(str1, str2): any {
    const matrix = [];
    
    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }
    
    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }
    
    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }
    
    return matrix[str2.length][str1.length];
  }
  
  /**
   * Rank results
   */
  rankResults(results, query): any {
    return results.sort((a, b) => {
      // Score based on multiple factors
      const scoreA = this.calculateRelevanceScore(a, query);
      const scoreB = this.calculateRelevanceScore(b, query);
      
      return scoreB - scoreA;
    });
  }
  
  /**
   * Calculate relevance score
   */
  calculateRelevanceScore(item, query): any {
    let score = 0;
    const queryLower = query.toLowerCase();
    
    // Name match (highest weight)
    if (item.name && item.name.toLowerCase().includes(queryLower)) {
      score += 10;
      if (item.name.toLowerCase() === queryLower) {
        score += 20; // Exact match
      }
    }
    
    // Description match
    if (item.description && item.description.toLowerCase().includes(queryLower)) {
      score += 5;
    }
    
    // Content match
    if (item.content && item.content.toLowerCase().includes(queryLower)) {
      score += 3;
    }
    
    // Tags match
    if (item.tags && Array.isArray(item.tags)) {
      if (item.tags.some(tag => tag.toLowerCase().includes(queryLower))) {
        score += 7;
      }
    }
    
    // Freshness score (if from external source)
    if (item._freshness) {
      score += item._freshness * 2;
    }
    
    // Recency (newer items score higher)
    if (item.createdAt) {
      const age = Date.now() - new Date(item.createdAt).getTime();
      const daysSinceCreation = age / (1000 * 60 * 60 * 24);
      score += Math.max(0, 5 - daysSinceCreation / 30); // Decay over 30 days
    }
    
    return score;
  }
  
  /**
   * Get searchable fields
   */
  getSearchableFields(item): any {
    return ['name', 'title', 'description', 'content', 'tags', 'notes'];
  }
  
  /**
   * Get field value
   */
  getFieldValue(item, field): any {
    if (Array.isArray(item[field])) {
      return item[field].join(' ');
    }
    return String(item[field] || '');
  }
  
  /**
   * Match filters
   */
  matchesFilters(item, filters): any {
    return Object.entries(filters).every(([key, value]) => {
      if (value === null || value === undefined || value === '') {
        return true;
      }
      
      if (Array.isArray(value)) {
        return value.includes(item[key]);
      }
      
      return item[key] === value;
    });
  }
  
  /**
   * Refresh adapter
   */
  async refreshAdapter(name): Promise<any> {
    const adapter = this.adapters.get(name);
    const freshness = this.freshnessScores.get(name);
    
    if (!adapter || !adapter.refresh) {
      return;
    }
    
    try {
      await adapter.refresh();
      freshness.lastRefresh = Date.now();
      freshness.score = 1.0;
    } catch (error) {
      console.error(`Failed to refresh ${name}:`, error);
      freshness.score = Math.max(0, freshness.score - 0.1);
    }
  }
  
  /**
   * Set freshness score manually
   */
  setFreshnessScore(adapterName: string, score): void {
    const freshness = this.freshnessScores.get(adapterName);
    if (freshness) {
      freshness.score = Math.max(0, Math.min(1, score));
    }
  }
  
  /**
   * Add to history
   */
  addToHistory(query: string, options: Record<string, unknown>): void {
    this.history.unshift({
      query,
      options,
      timestamp: Date.now()
    });
    
    if (this.history.length > this.maxHistorySize) {
      this.history = this.history.slice(0, this.maxHistorySize);
    }
  }
  
  /**
   * Save search
   */
  saveSearch(name: string, query, options: Record<string, unknown>): void {
    this.savedSearches.set(name, {
      query,
      options,
      createdAt: Date.now()
    });
  }
  
  /**
   * Get cache key
   */
  getCacheKey(query: string, options: Record<string, unknown>): any {
    return JSON.stringify({ query, options });
  }
  
  /**
   * Clear cache
   */
  clearCache(): void {
    this.cache.clear();
  }
  
  /**
   * Subscribe to events
   */
  subscribe(listener: Function): any {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
  
  /**
   * Notify listeners
   */
  notifyListeners(event, data): void {
    this.listeners.forEach(listener => {
      try {
        listener(event, data);
      } catch (error) {
        console.error('Searchr listener error:', error);
      }
    });
  }
}

export default SearchrManager;
